import { getApiUrl, getPlausibleAddress } from '../../config';
import { storage } from '../../lib/storage';
import { notify } from '../../utils/toast';
import algosdk from 'algosdk';
import { walletManager } from '../../services/wallet/walletConnection';

interface CreditPurchaseResponse {
  credit: number;
  status: string;
}

export const makePaymentTransaction = async (
  amount: number,
  from: string,
  algodClient: algosdk.Algodv2,
  signer: any // Accept any signer type to handle both old and new wallet APIs
): Promise<Uint8Array | null> => {
  const loadingToastId = notify.loading('Please sign the transaction in your wallet!');

  if (from === null || from === undefined || !algodClient || !signer) {
    console.error('Invalid information for payment transaction:', { amount, from, hasAlgodClient: !!algodClient, hasSigner: !!signer });
    notify.dismiss(loadingToastId);
    return null;
  }

  try {
    console.log('Getting transaction parameters...');
    const params = await algodClient.getTransactionParams().do();
    const plausibleAddress = getPlausibleAddress();

    if (!plausibleAddress) {
      throw new Error('Invalid Plausible address configuration');
    }

    console.log('Creating payment transaction to:', plausibleAddress);

    // Convert amount to microALGO (1 ALGO = 1,000,000 microALGO)
    const microAlgoAmount = amount * 1000000;

    const txn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
      suggestedParams: params,
      sender: from,
      receiver: plausibleAddress,
      amount: microAlgoAmount
    });

    console.log('Transaction created, requesting signature...');

    // Check if the signer is the new transactionSigner (accepts Transaction objects)
    // or the old signer (accepts Uint8Array)
    let signedTxns;

    // Check what type of signer we have
    console.log('Signer type:', typeof signer, signer && typeof signer.signTransactions);

    try {
      // Try the new transactionSigner approach first (from useWallet)
      console.log('Attempting to sign with transaction signer...');
      signedTxns = await signer([txn], [0]);
      console.log('Transaction signed successfully with transaction signer');
    } catch (error) {
      console.error('Error with transaction signer, trying fallback:', error);
      // Fall back to the old approach if the new one fails
      signedTxns = await signer([txn.toByte()], [0]);
      console.log('Transaction signed successfully with fallback signer');
    }

    if (!signedTxns || !signedTxns[0]) {
      throw new Error('Failed to sign transaction - no signed transaction returned');
    }

    const signedTxn = signedTxns[0];
    console.log('Transaction signed successfully');
    //await algodClient.sendRawTransaction(signedTxn).do();

    notify.dismiss(loadingToastId);
    return signedTxn;
  } catch (error: any) {
    console.error('Error in makePaymentTransaction:', error);
    notify.dismiss(loadingToastId);
    handleSubscriptionError(error);
    return null;
  }
};

export const processCreditPurchase = async (
  walletAddress: string,
  signedTxn: Uint8Array
): Promise<CreditPurchaseResponse | null> => {
  const loadingToastId = notify.loading('Processing credit purchase...');

  try {
    const profile = await storage.getProfile(walletAddress);
    if (!profile || !profile.jwt || !profile.nonce) {
      throw new Error('Profile data not found');
    }

    const apiUrl = getApiUrl();
    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(`${apiUrl}/credit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${profile.jwt}`
      },
      body: JSON.stringify({
        signedPayTxn: [uint8ToBase64(signedTxn)],
        nonce: profile.nonce
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to process credit purchase! Check your balance!');
    }

    const data = await response.json();

    // Update profile with new credit balance
    await storage.setProfile(walletAddress, {
      ...profile,
      credit: data.credit,
      nonce: generateNonce() // Generate new nonce for next transaction
    });

    notify.dismiss(loadingToastId);
    notify.success(`Successfully purchased credits! New balance: ${data.credit}`);

    return data;
  } catch (error: any) {
    notify.dismiss(loadingToastId);
    notify.error(error.message || 'Failed to process credit purchase! Check your balance!');
    return null;
  }
};
const normalizeParams = (params: Record<string, any>) => {
  return JSON.parse(
    JSON.stringify(params, (_, value) =>
      typeof value === "bigint" ? Number(value) : value
    )
  );
};

export const checkSubscription = async (
  walletAddress: string,
  signedTxn: Uint8Array,
  params: any,
  walletAddressRekeyTo: string | null | undefined = null,
  claimerData: any[] = [],
  creatorData: any[] = []
): Promise<any> => {
  const loadingToastId = notify.loading("Verifying subscription...");

  if (!walletAddress || !signedTxn || !params) {
    console.error("Invalid subscription check parameters");
    notify.dismiss(loadingToastId);
    return null;
  }

  try {
    const nonce = generateNonce();

    const txn = algosdk.decodeSignedTransaction(signedTxn);
    if (!txn || !txn.txn) {
      throw new Error("Invalid transaction data");
    }

    const apiUrl = getApiUrl();
    if (!apiUrl) {
      throw new Error("API URL not configured");
    }

    const txId = txn.txn.txID();

    const payload = {
      nonce,
      walletAddress,
      walletAddressRekeyTo: walletAddressRekeyTo || undefined,
      signedPayTxn: uint8ToBase64(signedTxn), // always base64 like UI
      txId,
      params: normalizeParams(params), // send raw params like UI
      claimerData,
      creatorData,
    };
    const body = JSON.stringify(payload)

    const response = await fetch(`${apiUrl}/subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        referer: new URL(apiUrl).host,
      },
      body: body,
    });

    const data = await handleSubscriptionResponse(response);

    if (data?.profile) {
      // Trim large arrays before storing
      delete data.profile.mints;
      delete data.profile.vcs;
      delete data.profile.auth_vcs;
      await storeProfileData(walletAddress, data);
    }

    notify.dismiss(loadingToastId);
    return data;
  } catch (error: any) {
    console.error("Subscription check error:", error);
    notify.dismiss(loadingToastId);
    notify.error(error.message || "Failed to verify subscription");
    return null;
  }
};

// Helper functions
const uint8ToBase64 = (uint8Array: Uint8Array) => {
  let binary = '';
  const len = uint8Array.length;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  const base64 = btoa(binary);
  // convert to Base64URL (no padding)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

const generateNonce = () => {
  return Array.from({ length: 6 }, () => Math.floor(Math.random() * 10)).join('');
};

const handleSubscriptionError = (error: any) => {
  if (error?.data?.type === "SIGN_MODAL_CLOSED") {
    notify.error('User refused to sign!');
  } else if (error.message?.includes('overspend')) {
    notify.error('Insufficient balance for subscription');
  } else if (error.message?.includes('rejected')) {
    notify.error('Transaction rejected by wallet');
  } else {
    console.error('Create subscription error:', error);
    notify.error('Failed to create subscription transaction');
  }
};

const handleSubscriptionResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403) {
      throw new Error(errorData.message || 'No active subscription found');
    }
    throw new Error(errorData.message || 'Failed to verify subscription');
  }
  return response.json();
};



const storeProfileData = async (walletAddress: string, data: any) => {
  const profileData = {
    ...data.profile,
    jwt: data.profile.token || data.profile.jwt,
    nonce: generateNonce(),
    credit: data.profile.credit || 0,
    connected: true, // Ensure connected flag is set
    address: walletAddress, // Ensure address is set
    did: data.profile.did || undefined // Preserve DID if it exists
  };

  console.log('Storing profile data:', profileData);

  await storage.setProfile(walletAddress, profileData);
  if (data.token) {
    localStorage.setItem('goplausible_token', data.token);
  }
};
